import { nonEmptyString } from '../../lib/format';
import React, { useState, useRef, useMemo } from 'react';
import { Button } from '../primitives';
import { Dialog } from '../Dialog';
import { confirmAction, promptText } from '../../lib/confirmation';
import { sanitizeNoteHtml, stripHtmlToText } from '../../lib/sanitize';
import type { WidgetPlacement, StrategicNote } from '../../types';

// ─── NotesWidget sub-components ──────────────────────────────────────────────
interface NotesWidgetConfigProps {
  widget: WidgetPlacement;
  onSave: (cfg: NonNullable<WidgetPlacement['config']>) => void;
  onCancel: () => void;
}

export function NotesWidgetConfig({ widget, onSave, onCancel }: NotesWidgetConfigProps): React.JSX.Element {
  const [form, setForm] = useState({
    showArchived: widget.config?.showArchived ?? false,
    authorNameFilter: widget.config?.authorNameFilter ?? '',
  });

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 8px', border: '1px solid var(--line)',
    borderRadius: '6px', fontSize: '12px', background: 'var(--page)', color: 'var(--ink)',
    marginBottom: '8px',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontSize: '12.5px' }}>
      <strong style={{ fontSize: '13px', marginBottom: '8px' }}>Configure Strategic Notes</strong>
      <div style={{ flex: 1 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer', fontWeight: 600 }}>
          <input type="checkbox" checked={form.showArchived} onChange={(e) => setForm((p) => ({ ...p, showArchived: e.target.checked }))} />
          Show Archived Notes by default
        </label>

        <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Filter by Author Name</label>
        <input type="text" value={form.authorNameFilter} placeholder="e.g. Sarah..." onChange={(e) => setForm((p) => ({ ...p, authorNameFilter: e.target.value }))} style={inputStyle} />
      </div>
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', paddingTop: '10px', borderTop: '1px solid var(--line)', marginTop: '8px' }}>
        <Button variant="secondary" type="submit"  onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit"  onClick={() => onSave(form)}>Save</Button>
      </div>
    </div>
  );
}

// Custom Note Editor Modal Sub-component
interface NoteEditorModalProps {
  note: StrategicNote | null; // null if creating new note
  onSave: (author: string, content: string) => void;
  onClose: () => void;
}

function NoteEditorModal({ note, onSave, onClose }: NoteEditorModalProps): React.JSX.Element {
  const [author, setAuthor] = useState(note?.author ?? 'Nicholas Matlach');
  const [content, setContent] = useState(note?.content ?? '');

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Helper to wrap selected text with HTML tags
  const wrapTag = (tag: string, attr = ''): void => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = ta.value;
    const selected = text.slice(start, end);
    const replacement = `<${tag}${attr}>${selected}</${tag}>`;
    const newContent = text.slice(0, start) + replacement + text.slice(end);
    setContent(newContent);
    
    // Reset selection focus
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + tag.length + 2 + attr.length + selected.length + tag.length + 3, start + tag.length + 2 + attr.length + selected.length + tag.length + 3);
    }, 10);
  };

  const insertLink = async (): Promise<void> => {
    const url = await promptText('Enter URL (e.g. https://example.com):');
    if (!url) return;
    wrapTag('a', ` href="${url}" target="_blank" style="color: var(--primary); text-decoration: underline;"`);
  };

  const insertList = (ordered = false): void => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = ta.value;
    const selected = text.slice(start, end);
    const listTag = ordered ? 'ol' : 'ul';
    const lines = selected.split('\n');
    const listItems = lines.map((l) => `  <li>${(nonEmptyString(l) ?? 'item')}</li>`).join('\n');
    const replacement = `\n<${listTag}>\n${listItems}\n</${listTag}>\n`;
    setContent(text.slice(0, start) + replacement + text.slice(end));
  };

  return (
    <Dialog label="Stage details" onClose={onClose} pending={false}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <h3 style={{ borderBottom: '1px solid var(--line)', paddingBottom: '8px', margin: 0 }}>
          {note ? '✏️ Edit Strategic Note' : '📝 Create Strategic Note'}
        </h3>
        
        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '12px', marginBottom: '3px' }}>Author / Creator Name</label>
          <input type="text" value={author} onChange={(e) => setAuthor(e.target.value)}
            style={{ width: '100%', padding: '6px', border: '1px solid var(--line)', borderRadius: '6px', fontSize: '12px' }} />
        </div>

        {/* Rich text helper toolbar */}
        <div style={{ display: 'flex', gap: '3px', background: 'var(--page)', border: '1px solid var(--line)', borderRadius: '6px 6px 0 0', padding: '4px', flexWrap: 'wrap', borderBottom: 'none' }}>
          <Button variant="secondary" type="submit" size="sm" onClick={() => wrapTag('b')} >B</Button>
          <Button variant="secondary" type="submit" size="sm" onClick={() => wrapTag('i')} style={{ fontStyle: 'italic' }}>I</Button>
          <Button variant="secondary" type="submit" size="sm" onClick={() => wrapTag('u')} style={{ textDecoration: 'underline' }}>U</Button>
          <Button variant="secondary" type="submit" size="sm" onClick={() => insertList(false)} >• List</Button>
          <Button variant="secondary" type="submit" size="sm" onClick={() => insertList(true)} >1. List</Button>
          <Button variant="secondary" type="submit" size="sm" onClick={() => { void insertLink(); }} >🔗 Link</Button>
        </div>

        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Write strategic note content... (HTML formatting is supported)"
          rows={8}
          style={{ width: '100%', padding: '8px', border: '1px solid var(--line)', borderRadius: '0 0 6px 6px', fontSize: '12.5px', fontFamily: 'monospace', resize: 'vertical' }}
        />

        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', marginTop: '6px' }}>
          <Button variant="secondary" type="submit"  onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit"  onClick={() => onSave(author, content)}>Save Note</Button>
        </div>
      </div>
    </Dialog>
  );
}

// Note detail popup modal
interface NoteViewModalProps {
  note: StrategicNote;
  onClose: () => void;
}

function NoteViewModal({ note, onClose }: NoteViewModalProps): React.JSX.Element {
  return (
    <Dialog label="Stage details" onClose={onClose} pending={false}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '90%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--line)', paddingBottom: '8px', marginBottom: '10px' }}>
          <strong style={{ fontSize: '13px' }}>👤 {note.author}</strong>
          <span style={{ fontSize: '12px', color: 'var(--muted)' }}>{note.date}</span>
        </div>
        <div
          style={{ fontSize: '13px', lineHeight: 1.4, wordBreak: 'break-word', minHeight: '80px' }}
          dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(note.content) }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
          <Button variant="secondary" type="submit"  onClick={onClose}>Close</Button>
        </div>
      </div>
    </Dialog>
  );
}

interface NotesWidgetBodyProps {
  widget: WidgetPlacement;
  notes: StrategicNote[];
  onUpdateNotes: (notes: StrategicNote[]) => void;
}

export function NotesWidgetBody({ widget, notes, onUpdateNotes }: NotesWidgetBodyProps): React.JSX.Element {
  const cfg = widget.config ?? {};
  const [showArchivedLocal, setShowArchivedLocal] = useState<boolean>(cfg.showArchived ?? false);
  const [search, setSearch] = useState('');

  // Expand / Collapse state (key: noteId -> true for 3-rows expanded)
  const [expandedNotes, setExpandedNotes] = useState<Record<string, boolean>>({});

  // Modals state
  const [viewNote, setViewNote] = useState<StrategicNote | null>(null);
  const [editNote, setEditNote] = useState<StrategicNote | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const toggleExpand = (noteId: string): void => {
    setExpandedNotes((prev) => ({ ...prev, [noteId]: !prev[noteId] }));
  };

  const handleToggleArchive = (noteId: string): void => {
    const updated = notes.map((n) => (n.id === noteId ? { ...n, is_archived: !n.is_archived } : n));
    onUpdateNotes(updated);
  };

  const handleDelete = async (noteId: string): Promise<void> => {
    if (await confirmAction('Delete this strategic note permanently?')) {
      const updated = notes.filter((n) => n.id !== noteId);
      onUpdateNotes(updated);
    }
  };

  const handleSaveEdit = (author: string, content: string): void => {
    if (editNote) {
      const updated = notes.map((n) => (n.id === editNote.id ? { ...n, author, content, date: new Date().toISOString().split('T')[0]! } : n));
      onUpdateNotes(updated);
      setEditNote(null);
    }
  };

  const handleSaveCreate = (author: string, content: string): void => {
    const newNote: StrategicNote = {
      id: `note_${Date.now()}`,
      author,
      content,
      date: new Date().toISOString().split('T')[0]!,
      is_archived: false,
    };
    onUpdateNotes([newNote, ...notes]);
    setCreateOpen(false);
  };

  // Filter notes
  const filtered = useMemo(() => {
    let out = [...notes];
    if (!showArchivedLocal) {
      out = out.filter((n) => !n.is_archived);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((n) => n.content.toLowerCase().includes(q) || n.author.toLowerCase().includes(q));
    }
    if (cfg.authorNameFilter?.trim()) {
      const f = cfg.authorNameFilter.trim().toLowerCase();
      out = out.filter((n) => n.author.toLowerCase().includes(f));
    }
    return out;
  }, [notes, showArchivedLocal, search, cfg.authorNameFilter]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* 1. Header Toolbar */}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '8px', flexShrink: 0 }}>
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter notes..."
          style={{ flex: 1, padding: '4px 8px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: '6px', background: 'var(--page)' }}
        />
        <button
          onClick={() => setShowArchivedLocal((v) => !v)}
          style={{
            padding: '4px 8px', fontSize: '12px', fontWeight: 'bold', borderRadius: '6px', cursor: 'pointer',
            background: showArchivedLocal ? 'var(--primary-soft)' : 'var(--page)',
            color: showArchivedLocal ? 'var(--primary)' : 'var(--muted)',
            border: `1px solid ${showArchivedLocal ? 'var(--primary)' : 'var(--line)'}`,
          }}
        >
          📂 Show Archived
        </button>
        <Button variant="primary" type="submit" size="sm"
          onClick={() => setCreateOpen(true)}
          
        >
          ＋ Note
        </Button>
      </div>

      {/* 2. Scrollable Notes Rows */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {filtered.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: '12px', textAlign: 'center', padding: '24px 0' }}>
            No strategic notes found.
          </div>
        ) : (
          filtered.map((note) => {
            const isExpanded = (expandedNotes[note.id] ?? false);
            // Clean plain text representation of the HTML note content for collapsed ellipsis display
            const plainText = stripHtmlToText(note.content);

            return (
              <div key={note.id} style={{ padding: '6px 8px', background: note.is_archived ? '#f1f5f9' : '#fff', border: '1px solid var(--line)', borderRadius: '8px', transition: 'all 0.15s' }}>
                {/* Note Row Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', borderBottom: '1px dashed var(--line-soft)', paddingBottom: '3px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--ink)' }}>
                    👤 {note.author} {note.is_archived && <span style={{ color: 'var(--muted)', fontStyle: 'italic', fontWeight: 'normal' }}>(Archived)</span>}
                  </span>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>{note.date}</span>
                    <Button variant="ghost" type="submit" size="sm" onClick={() => setEditNote(note)}  title="Edit">✏️</Button>
                    <Button variant="ghost" type="submit" size="sm" onClick={() => handleToggleArchive(note.id)}  title={note.is_archived ? "Unarchive" : "Archive"}>📦</Button>
                    <Button variant="ghost" type="submit" size="sm" onClick={() => void handleDelete(note.id)}  title="Delete">🗑️</Button>
                  </div>
                </div>

                {/* Collapsible Content */}
                {!isExpanded ? (
                  /* Single collapsed row */
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <span style={{ flex: 1, fontSize: '12px', color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {plainText}
                    </span>
                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                      <Button variant="ghost" type="submit" size="sm" onClick={() => toggleExpand(note.id)} >More</Button>
                      <Button variant="ghost" type="submit" size="sm" onClick={() => setViewNote(note)} >Popout</Button>
                    </div>
                  </div>
                ) : (
                  /* Expanded to 3 rows or full rich text */
                  <div>
                    <div
                      style={{
                        fontSize: '12px', color: 'var(--ink)', lineHeight: 1.3,
                        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                        overflow: 'hidden', wordBreak: 'break-word', marginBottom: '4px'
                      }}
                      dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(note.content) }}
                    />
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', borderTop: '1px solid #f1f5f9', paddingTop: '2px' }}>
                      <Button variant="ghost" type="submit" size="sm" onClick={() => toggleExpand(note.id)} >Less</Button>
                      <Button variant="ghost" type="submit" size="sm" onClick={() => setViewNote(note)} >Popout</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Note view modal */}
      {viewNote && <NoteViewModal note={viewNote} onClose={() => setViewNote(null)} />}

      {/* Note edit modal */}
      {editNote && <NoteEditorModal note={editNote} onSave={handleSaveEdit} onClose={() => setEditNote(null)} />}

      {/* Note create modal */}
      {createOpen && <NoteEditorModal note={null} onSave={handleSaveCreate} onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

